import React, { Fragment } from 'react';

import { Calendar, Card, Col, DatePicker, Row, Select, Typography } from 'antd';

import { Column, Pie } from '@ant-design/charts';
import { CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleFilled, ExclamationCircleOutlined, SolutionOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import Authentication from 'lib/Authentication';
import { makeFilterOption } from 'lib/helpers/Form.helper';
import { Indicator } from 'lib/Indicator';
import { DashboardContextProvider } from 'lib/providers/DashboardContextProvider';

export function Dashboard() {
    const makeFilter = makeFilterOption();
    const clients = Authentication.getClients().map((client) => ({
        label: client.name,
        value: client.id
    }));

    return (
        <DashboardContextProvider>
            {({ isLoading, issuesIndicator, maintenancesIndicator, schedule, filters, setFilters }) => (
                <Fragment>
                    <Row>
                        <Col span={24}>
                            <Typography.Title level={3}>Dashboard</Typography.Title>
                            <Typography.Text type="secondary">
                                Indicadores de chamados e manutenções
                            </Typography.Text>
                        </Col>
                    </Row>
                    <Card title="Filtros" loading={isLoading}>
                        <Row>
                            <Col xs={24} sm={24} md={8} lg={8}>
                                <Select
                                    style={{ width: '100%' }}
                                    placeholder="Condomínio"
                                    allowClear
                                    showSearch
                                    onChange={(value) => setFilters({ ...filters, client_id: value })}
                                    value={filters?.client_id}
                                    options={clients}
                                    filterOption={makeFilter}
                                />
                            </Col>
                            <Col xs={24} sm={24} md={8} lg={8}>
                                <DatePicker
                                    style={{ width: '100%' }}
                                    placeholder="Data inicial"
                                    allowClear
                                    onChange={(date) => setFilters({ ...filters, start_date: date?.format('YYYY-MM-DD') })}
                                    value={filters?.start_date ? dayjs(filters.start_date) : null}
                                    format='DD/MM/YYYY'
                                />
                            </Col>
                            <Col xs={24} sm={24} md={8} lg={8}>
                                <DatePicker
                                    style={{ width: '100%' }}
                                    placeholder="Data final"
                                    allowClear
                                    onChange={(date) => setFilters({ ...filters, end_date: date?.format('YYYY-MM-DD') })}
                                    value={filters?.end_date ? dayjs(filters.end_date) : null}
                                    format='DD/MM/YYYY'
                                />
                            </Col>
                        </Row>
                    </Card>
                    <Row>
                        <Col span={24}>
                            <Typography.Title level={4}>Chamados</Typography.Title>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={24} sm={24} md={6} lg={6}>
                            <Indicator
                                icon={<CheckCircleOutlined />}
                                loading={isLoading}
                                iconBackgroundColor='#6abe25'
                                label='Concluídos'
                                value={issuesIndicator.issues_by_status?.closed ?? 0} />
                        </Col>
                        <Col xs={24} sm={24} md={6} lg={6}>
                            <Indicator
                                label="Realizados dentro do prazo"
                                loading={isLoading}
                                value={issuesIndicator.issues_resolved_on_time_and_late?.on_time ?? 0}
                                icon={<ClockCircleOutlined />}
                                iconBackgroundColor='#0B1A62'
                            />
                        </Col>
                        <Col xs={24} sm={24} md={6} lg={6}>
                            <Indicator
                                label="Realizados fora do prazo"
                                loading={isLoading}
                                value={issuesIndicator.issues_resolved_on_time_and_late?.late ?? 0}
                                icon={<ClockCircleOutlined />}
                                iconBackgroundColor='#ff4d4f'
                            />
                        </Col>
                        <Col xs={24} sm={24} md={6} lg={6}>
                            <Indicator
                                label="TMR (Tempo Médio de Resolução)"
                                loading={isLoading}
                                value={issuesIndicator.issue_avarage_time_to_resolve ? `${Math.round(issuesIndicator.issue_avarage_time_to_resolve)} dia(s)` : 'Sem dados'}
                                icon={<ClockCircleOutlined />}
                                iconBackgroundColor='#0B1A62'
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={24} sm={24} md={6} lg={6}>
                            <Indicator
                                icon={<ClockCircleOutlined />}
                                loading={isLoading}
                                iconBackgroundColor='#ff4d4f'
                                label='Atrasados'
                                value={issuesIndicator.issues_by_status?.overdue ?? 0} />
                        </Col>
                        <Col xs={24} sm={24} md={6} lg={6}>
                            <Indicator
                                icon={<CheckCircleOutlined />}
                                loading={isLoading}
                                iconBackgroundColor='#1890ff'
                                label='Em andamento'
                                value={issuesIndicator.issues_by_status?.in_progress ?? 0} />
                        </Col>
                        <Col xs={24} sm={24} md={6} lg={6}>
                            <Indicator
                                icon={<ExclamationCircleOutlined />}
                                loading={isLoading}
                                iconBackgroundColor='#FA8C16'
                                label='Abertos'
                                value={issuesIndicator.issues_by_status?.opened ?? 0} />
                        </Col>
                        <Col xs={24} sm={24} md={6} lg={6}>
                            <Indicator
                                icon={<SolutionOutlined />}
                                loading={isLoading}
                                iconBackgroundColor='#e3b51e'
                                label='Aguardando assembleia'
                                value={issuesIndicator.issues_by_status?.waiting_ogm ?? 0} />
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <Card>
                                <Column
                                    data={issuesIndicator.issues_by_month_and_year ?? []}
                                    isGroup={true}
                                    xField='name'
                                    yField='value'
                                    seriesField='label'
                                    height={400}
                                    loading={isLoading}
                                />
                            </Card>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <Card>
                                <Pie
                                    data={issuesIndicator.issues_by_type ?? []}
                                    appendPadding={10}
                                    angleField='value'
                                    colorField='name'
                                    radius={0.9}
                                    label={{
                                        type: 'inner',
                                        offset: '-30%',
                                        content: ({ percent }: any) => `${(percent * 100).toFixed(0)}%`,
                                        style: {
                                            fontSize: 14,
                                            textAlign: 'center',
                                        },
                                    }}
                                    interactions={[{ type: 'element-active' }]}
                                    loading={isLoading}
                                />
                            </Card>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <Typography.Title level={4}>Manutenções</Typography.Title>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={24} sm={24} md={6} lg={6}>
                            <Indicator
                                icon={<CheckCircleOutlined />}
                                loading={isLoading}
                                iconBackgroundColor='#6abe25'
                                label='Concluídas'
                                value={maintenancesIndicator.maintenances_by_status?.finished ?? 0} />
                        </Col>
                        <Col xs={24} sm={24} md={9} lg={9}>
                            <Indicator
                                label="Realizadas dentro do prazo"
                                loading={isLoading}
                                value={maintenancesIndicator.maintenances_resolved_on_time_and_late?.on_time ?? 0}
                                icon={<ClockCircleOutlined />}
                                iconBackgroundColor='#0B1A62'
                            />
                        </Col>
                        <Col xs={24} sm={24} md={9} lg={9}>
                            <Indicator
                                label="Realizadas fora do prazo"
                                loading={isLoading}
                                value={maintenancesIndicator.maintenances_resolved_on_time_and_late?.late ?? 0}
                                icon={<ClockCircleOutlined />}
                                iconBackgroundColor='#ff4d4f'
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={24} sm={24} md={6} lg={6}>
                            <Indicator
                                icon={<ClockCircleOutlined />}
                                loading={isLoading}
                                label='Atrasadas'
                                value={maintenancesIndicator.maintenances_by_status?.overdue ?? 0}
                                iconBackgroundColor='#ff4d4f'
                            />
                        </Col>
                        <Col xs={24} sm={24} md={6} lg={6}>
                            <Indicator
                                icon={<ExclamationCircleFilled />}
                                loading={isLoading}
                                label='Vencem este mês'
                                value={maintenancesIndicator.maintenances_by_status?.expires_this_month ?? 0}
                                iconBackgroundColor='#ff4d4f'
                            />
                        </Col>
                        <Col xs={24} sm={24} md={6} lg={6}>
                            <Indicator
                                icon={<ExclamationCircleOutlined />}
                                loading={isLoading}
                                label='Vencem no próximo mês'
                                value={maintenancesIndicator.maintenances_by_status?.expires_next_month ?? 0}
                                iconBackgroundColor='#FA8C16'
                            />
                        </Col>
                        <Col xs={24} sm={24} md={6} lg={6}>
                            <Indicator
                                icon={<ClockCircleOutlined />}
                                loading={isLoading}
                                label='Dentro do prazo'
                                value={maintenancesIndicator.maintenances_by_status?.on_time ?? 0}
                                iconBackgroundColor='#6abe25'
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <Card>
                                <Column
                                    data={maintenancesIndicator.maintenances_by_month_and_year ?? []}
                                    isGroup={true}
                                    xField='name'
                                    yField='value'
                                    seriesField='label'
                                    height={400}
                                    loading={isLoading}
                                />
                            </Card>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <Card>
                                <Pie
                                    data={maintenancesIndicator.maintenances_by_type ?? []}
                                    appendPadding={10}
                                    angleField='value'
                                    colorField='name'
                                    radius={0.9}
                                    label={{
                                        type: 'inner',
                                        offset: '-30%',
                                        content: ({ percent }: any) => `${(percent * 100).toFixed(0)}%`,
                                        style: {
                                            fontSize: 14,
                                            textAlign: 'center',
                                        },
                                    }}
                                    interactions={[{ type: 'element-active' }]}
                                    loading={isLoading}
                                />
                            </Card>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <Typography.Title level={4}>Cronograma de chamados e manutenções</Typography.Title>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <Card loading={isLoading}>
                                <Calendar
                                    fullscreen={true}
                                    cellRender={((date, info) => {
                                        const mode = info?.type === 'month' ? 'month' : 'date';
                                        const items = schedule.filter((item) => dayjs(item.date).isSame(date, mode));

                                        return (
                                            <div>
                                                {items.length > 0 && (
                                                    items.map((item) => <Row key={item.id}>
                                                        <Typography.Text>{item.client}: </Typography.Text>
                                                        <Typography.Text type="secondary">{item.title}</Typography.Text>
                                                    </Row>
                                                    )
                                                )}
                                            </div>
                                        );
                                    })}
                                />
                            </Card>
                        </Col>
                    </Row>
                </Fragment>
            )
            }
        </DashboardContextProvider >
    );
}